// src/services/asteriskService.js
import axios from 'axios';
import { ASTERISK_API_BASE } from '../config/bridge';
import { parseJwt } from './api';

const parseTimeout = (value, fallback) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return fallback;
  return Math.round(num);
};

const API_TIMEOUT_MS = parseTimeout(process.env.REACT_APP_ASTERISK_TIMEOUT_MS, 8000);
const QUEUE_METRICS_TIMEOUT_MS = parseTimeout(
  process.env.REACT_APP_ASTERISK_QUEUE_METRICS_TIMEOUT_MS,
  Math.max(API_TIMEOUT_MS, 15000)
);
const HEALTH_CACHE_MS = parseTimeout(process.env.REACT_APP_ASTERISK_HEALTH_CACHE_MS, 1500);

const normalizeExtension = (value) => {
  const trimmed = String(value || '').trim();
  return /^[0-9]+$/.test(trimmed) ? trimmed : '';
};

const readStoredExtension = () => {
  if (typeof window === 'undefined') return '';
  try {
    const ext = JSON.parse(window.localStorage.getItem('extension') || '{}');
    return normalizeExtension(
      ext.extensionNumber ||
      ext.ExtensionNumber ||
      ext.extension ||
      ext.Extension ||
      ext.sipUsername ||
      ext.SIPUsername ||
      ''
    );
  } catch {
    return '';
  }
};

const getSipResponseText = (code, text) => {
  const normalizedCode = String(code || '').trim();
  const normalizedText = String(text || '').trim();
  if (!normalizedCode) return '';
  return `${normalizedCode}${normalizedText ? ` ${normalizedText}` : ''}`;
};

const toBoolean = (value) => {
  if (value === true || value === 1) return true;
  const normalized = String(value || '').trim().toLowerCase();
  return ['true', '1', 'yes'].includes(normalized);
};

const isSipRingbackResponse = (code) => String(code || '').trim() === '180';

const getCallResponseFields = (data = {}) => {
  const sipResponseCode = String(data.sipResponseCode || '').trim();
  const sipResponseText = String(data.sipResponseText || '').trim();
  const sipProgressCode = String(data.sipProgressCode || '').trim();
  const sipProgressText = String(data.sipProgressText || '').trim();

  return {
    dialStatus: String(data.dialStatus || '').trim(),
    sipProgressCode,
    sipProgressText,
    sipProgress: String(data.sipProgress || getSipResponseText(sipProgressCode, sipProgressText)).trim(),
    sipProgressAt: String(data.sipProgressAt || '').trim(),
    hasSipProgress: toBoolean(data.hasSipProgress) || Boolean(sipProgressCode),
    hasSipRingbackEvent: toBoolean(data.hasSipRingbackEvent) || isSipRingbackResponse(sipProgressCode),
    sipResponseCode,
    sipResponseText,
    sipResponse: String(data.sipResponse || getSipResponseText(sipResponseCode, sipResponseText)).trim(),
    q850Cause: String(data.q850Cause || '').trim(),
    q850Text: String(data.q850Text || '').trim()
  };
};

const apiClient = axios.create({
  baseURL: ASTERISK_API_BASE,
  timeout: API_TIMEOUT_MS
});

// Attach JWT token to Asterisk API requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

class AsteriskService {
  constructor() {
    this.activeChannels = new Map();
    this.connectionStatus = 'disconnected';
    this.healthCheckInterval = null;
    this.pendingHealthRequest = null;
    this.lastHealthSnapshot = null;
    this.lastHealthFetchedAt = 0;
    this.agentContext = { agentId: '', status: '', direction: '' };
    this.statusPollers = new Map();
    this.statusRequestsInFlight = new Set();
    this.startHealthCheck();
  }

  // Start periodic health checks
  startHealthCheck() {
    // avoid duplicate intervals
    this.stopHealthCheck();
    this.healthCheckInterval = setInterval(async () => {
      try {
        const health = await this.getHealth({ maxAgeMs: HEALTH_CACHE_MS });
        this.connectionStatus = health.ariConnected ? 'connected' : 'disconnected';
      } catch (error) {
        this.connectionStatus = 'disconnected';
      }
    }, 5000); // Check every 5 seconds
  }

  // Stop health checks
  stopHealthCheck() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  // Get server health status
  async getHealth(options = {}) {
    const { force = false, maxAgeMs = HEALTH_CACHE_MS } = options;
    const now = Date.now();

    if (!force && this.lastHealthSnapshot && now - this.lastHealthFetchedAt <= maxAgeMs) {
      return this.lastHealthSnapshot;
    }

    if (this.pendingHealthRequest) {
      return this.pendingHealthRequest;
    }

    try {
      this.pendingHealthRequest = apiClient.get('/health')
        .then((response) => {
          this.connectionStatus = response.data?.ariConnected ? 'connected' : 'disconnected';
          this.lastHealthSnapshot = response.data;
          this.lastHealthFetchedAt = Date.now();
          return response.data;
        })
        .finally(() => {
          this.pendingHealthRequest = null;
        });

      return await this.pendingHealthRequest;
    } catch (error) {
      throw error;
    }
  }

  async getMusicOnHoldInfo() {
    const response = await apiClient.get('/admin/music-on-hold');
    return response.data;
  }

  async replaceMusicOnHold(file) {
    const response = await apiClient.post(
      `/admin/music-on-hold/replace?filename=${encodeURIComponent(file.name)}`,
      file,
      {
        headers: { 'Content-Type': 'application/octet-stream' },
        timeout: 60000
      }
    );
    return response.data;
  }

  // Make a call
  async makeCall(number, callerId = null, userId = null, agentId = null) {
    try {
      const assignedExtension = normalizeExtension(parseJwt(localStorage.getItem('token') || '')?.extension);
      const resolvedAgentId = assignedExtension || normalizeExtension(
        agentId ||
        this.agentContext?.agentId ||
        readStoredExtension()
      );
      if (!resolvedAgentId) {
        return {
          success: false,
          error: 'No SIP extension found for this agent. Please register the softphone before making an outbound call.',
          code: 'AGENT_EXTENSION_REQUIRED'
        };
      }
      console.log(`📞 Making call to: ${number} for user: ${userId}, agent: ${resolvedAgentId}`);
      const response = await apiClient.post('/call', {
        number: number.toString(),
        callerId,
        userId: userId || resolvedAgentId || `user_${Date.now()}`,
        agentId: resolvedAgentId
      });
      
      const channelId = response.data.channelId;
      this.activeChannels.set(channelId, {
        number,
        calledNumber: number,
        userId: response.data.userId,
        startTime: new Date(),
        status: 'dialing',
        duration: 0,
        callerNumber: number,
        acceptedBy: '',
        acceptedAt: '',
        queryId: '',
        ivrUid: '',
        linkedId: '',
        stableId: channelId,
        ...getCallResponseFields()
      });
      
      console.log(`✅ Call initiated. Channel ID: ${channelId}`);
      
      // Start polling for status updates
      this.startStatusPolling(channelId);
      
      return {
        success: true,
        channelId,
        number,
        userId: response.data.userId,
        agentId: response.data.agentId
      };
    } catch (error) {
      console.error('Call failed:', error);
      
      // Handle specific error for multiple calls
      if (error.response?.status === 409 && (
        error.response.data?.code === 'CALL_ALREADY_ACTIVE' ||
        (Array.isArray(error.response.data?.activeChannels) && error.response.data.activeChannels.length > 0)
      )) {
        return {
          success: false,
          error: error.response.data.error,
          code: 'CALL_ALREADY_ACTIVE',
          detail: error.response.data.detail
        };
      }
      
      const errorMessage = error.response?.data?.detail || error.response?.data?.error || error.response?.data?.message || error.message || 'Call failed';
      const reason = String(error.response?.data?.reason || '').trim();
      return {
        success: false,
        error: reason ? `${errorMessage}: ${reason}` : errorMessage,
        code: error.response?.data?.code
      };
    }
  }

  // Start polling channel status
  startStatusPolling(channelId) {
    if (!channelId) return null;
    if (this.statusPollers.has(channelId)) {
      return this.statusPollers.get(channelId);
    }

    let lastStatus = null;
    let lastResponseKey = '';
    const pollStatus = async () => {
      if (this.statusRequestsInFlight.has(channelId)) {
        return;
      }

      this.statusRequestsInFlight.add(channelId);
      try {
        const response = await apiClient.get(`/channels/${channelId}/status`);
        const status = response.data.status;
        const normalizedStatus = String(status || '').trim().toLowerCase();
        const isTerminalStatus = ['ended', 'idle', 'disconnected', 'failed', 'feedback'].includes(normalizedStatus);
        let channelInfo = this.activeChannels.get(channelId);

        if (!channelInfo) {
          channelInfo = {
            number: response.data.callerNumber || response.data.calledNumber || '',
            calledNumber: response.data.calledNumber || '',
            userId: response.data.userId || '',
            startTime: response.data.startTime ? new Date(response.data.startTime) : new Date(),
            status,
            duration: Number(response.data.duration || 0),
            isRecording: Boolean(response.data.isRecording),
            recordingName: response.data.recordingName || '',
            recordingStartedAt: response.data.recordingStartedAt || '',
            callerNumber: response.data.callerNumber || '',
            acceptedBy: response.data.acceptedBy || '',
            acceptedAt: response.data.acceptedAt || '',
            queryId: response.data.queryId || '',
            ivrUid: response.data.ivrUid || '',
            linkedId: response.data.linkedId || '',
            stableId: response.data.stableId || response.data.linkedId || response.data.ivrUid || channelId,
            ...getCallResponseFields(response.data)
          };
        } else {
          channelInfo.number = response.data.callerNumber || response.data.calledNumber || channelInfo.number || '';
          channelInfo.calledNumber = response.data.calledNumber || channelInfo.calledNumber || '';
          if (response.data.startTime) {
            channelInfo.startTime = new Date(response.data.startTime);
          }
          channelInfo.status = status;
          channelInfo.duration = Number(response.data.duration || 0);
          channelInfo.isRecording = Boolean(response.data.isRecording);
          channelInfo.recordingName = response.data.recordingName || '';
          channelInfo.recordingStartedAt = response.data.recordingStartedAt || '';
          channelInfo.callerNumber = response.data.callerNumber || channelInfo.callerNumber || '';
          channelInfo.acceptedBy = response.data.acceptedBy || channelInfo.acceptedBy || '';
          channelInfo.acceptedAt = response.data.acceptedAt || channelInfo.acceptedAt || '';
          channelInfo.queryId = response.data.queryId || channelInfo.queryId || '';
          channelInfo.ivrUid = response.data.ivrUid || channelInfo.ivrUid || '';
          channelInfo.linkedId = response.data.linkedId || channelInfo.linkedId || '';
          channelInfo.stableId =
            response.data.stableId ||
            response.data.linkedId ||
            response.data.ivrUid ||
            channelInfo.stableId ||
            channelId;
          Object.assign(channelInfo, getCallResponseFields(response.data));
        }

        channelInfo.pollInterval = pollInterval;
        this.activeChannels.set(channelId, channelInfo);

        const responseKey = [
          channelInfo.dialStatus || '',
          channelInfo.sipProgressCode || '',
          channelInfo.sipProgressText || '',
          channelInfo.sipProgressAt || '',
          channelInfo.sipResponseCode || '',
          channelInfo.sipResponseText || '',
          channelInfo.q850Cause || '',
          channelInfo.q850Text || ''
        ].map((value) => String(value || '').trim()).join('|');
        const responseChanged = responseKey && responseKey !== lastResponseKey;

        if (this.onStatusChange && status && (status !== lastStatus || responseChanged)) {
          this.onStatusChange(channelId, status, { channelId, ...channelInfo });
        }
        lastStatus = status;
        lastResponseKey = responseKey;
        if (isTerminalStatus) {
          this.stopStatusPolling(channelId);
        }
      } catch (error) {
        // Channel might be gone, stop polling
        this.stopStatusPolling(channelId);
        const responseStatus = error?.response?.status;
        const responseCode = String(error?.response?.data?.code || '').trim();
        if (
          this.onStatusChange &&
          (responseStatus === 404 || (responseStatus === 403 && responseCode === 'CHANNEL_FORBIDDEN'))
        ) {
          this.onStatusChange(channelId, 'idle');
        }
      } finally {
        this.statusRequestsInFlight.delete(channelId);
      }
    };

    const pollInterval = setInterval(pollStatus, 500);
    this.statusPollers.set(channelId, pollInterval);
    
    // Store interval reference for cleanup
    const channelInfo = this.activeChannels.get(channelId);
    if (channelInfo) {
      channelInfo.pollInterval = pollInterval;
    }

    void pollStatus();
    return pollInterval;
  }

  stopStatusPolling(channelId) {
    const pollInterval = this.statusPollers.get(channelId);
    if (pollInterval) {
      clearInterval(pollInterval);
      this.statusPollers.delete(channelId);
    }

    const channelInfo = this.activeChannels.get(channelId);
    if (channelInfo?.pollInterval && channelInfo.pollInterval !== pollInterval) {
      clearInterval(channelInfo.pollInterval);
    }
    this.statusRequestsInFlight.delete(channelId);
    this.activeChannels.delete(channelId);
  }

  resetCurrentCall(channelId = '') {
    if (channelId) {
      this.stopStatusPolling(channelId);
      return;
    }

    for (const activeChannelId of this.statusPollers.keys()) {
      this.stopStatusPolling(activeChannelId);
    }

    for (const [activeChannelId, channelInfo] of this.activeChannels.entries()) {
      if (channelInfo?.pollInterval) {
        clearInterval(channelInfo.pollInterval);
      }
      this.activeChannels.delete(activeChannelId);
    }
  }

  // Set status change callback
  setStatusChangeCallback(callback) {
    this.onStatusChange = callback;
  }

  // Set current agent context so the backend can filter calls
  setAgentContext(context = {}) {
    const prev = this.agentContext || {};
    const next = { ...prev, ...context };
    this.agentContext = next;

    const agentId = String(next.agentId || '').trim();
    const status = String(next.status || '').trim();
    const cmsUserId = String(next.cmsUserId || '').trim();
    const cmsUserName = String(next.cmsUserName || '').trim();
    const direction = String(next.direction || '').trim();
    const queueName = String(next.queueName || '').trim();
    const queueNames = Array.isArray(next.queueNames)
      ? next.queueNames.map((name) => String(name || '').trim()).filter(Boolean)
      : [];
    const queueKey = queueNames.length ? queueNames.join(',') : queueName;
    const prevAgentId = String(prev.agentId || '').trim();
    const prevStatus = String(prev.status || '').trim();
    const prevCmsUserId = String(prev.cmsUserId || '').trim();
    const prevCmsUserName = String(prev.cmsUserName || '').trim();
    const prevDirection = String(prev.direction || '').trim();
    const prevQueueName = String(prev.queueName || '').trim();
    const prevQueueNames = Array.isArray(prev.queueNames)
      ? prev.queueNames.map((name) => String(name || '').trim()).filter(Boolean)
      : [];
    const prevQueueKey = prevQueueNames.length ? prevQueueNames.join(',') : prevQueueName;

    if (!agentId) return;
    if (
      agentId !== prevAgentId ||
      status !== prevStatus ||
      cmsUserId !== prevCmsUserId ||
      cmsUserName !== prevCmsUserName ||
      direction !== prevDirection ||
      queueKey !== prevQueueKey
    ) {
      this.updateAgentStatus(agentId, status || 'idle', { cmsUserId, cmsUserName, direction, queueName, queueNames });
    }
  }

  async updateAgentStatus(agentId, status, extra = {}) {
    try {
      const payload = { status: status || 'idle' };
      if (extra.cmsUserId !== undefined && extra.cmsUserId !== null && String(extra.cmsUserId).trim() !== '') {
        payload.cmsUserId = String(extra.cmsUserId).trim();
      }
      if (extra.cmsUserName !== undefined && extra.cmsUserName !== null && String(extra.cmsUserName).trim() !== '') {
        payload.cmsUserName = String(extra.cmsUserName).trim();
      }
      if (extra.direction !== undefined && extra.direction !== null && String(extra.direction).trim() !== '') {
        payload.direction = String(extra.direction).trim();
      }
      if (extra.queueName !== undefined && extra.queueName !== null && String(extra.queueName).trim() !== '') {
        payload.queueName = String(extra.queueName).trim();
      }
      if (Array.isArray(extra.queueNames)) {
        const queueNames = extra.queueNames.map((name) => String(name || '').trim()).filter(Boolean);
        if (queueNames.length) payload.queueNames = queueNames;
      }
      const response = await apiClient.put(`/users/${encodeURIComponent(agentId)}/status`, {
        ...payload
      });
      return response.data || { success: true };
    } catch (error) {
      console.warn('Agent status update failed:', error?.message || error);
      return null;
    }
  }

  getAgentContext() {
    return this.agentContext;
  }

  // Get incoming calls
  async getIncomingCalls() {
    if (!this.isConnected()) {
      return {
        success: false,
        offline: true,
        error: 'Asterisk server not connected',
        calls: [],
        count: 0
      };
    }

    try {
      const params = new URLSearchParams();
      const agentId = String(this.agentContext?.agentId || '').trim();
      if (agentId) params.set('agentId', agentId);
      const url = params.toString() ? `/incoming-calls?${params.toString()}` : '/incoming-calls';
      const response = await apiClient.get(url);
      return {
        success: true,
        calls: response.data.incomingCalls || [],
        count: response.data.count || 0
      };
    } catch (error) {
      const message = String(error?.message || '');
      const isTimeout =
        error?.code === 'ECONNABORTED' ||
        message.toLowerCase().includes('timeout') ||
        message.toLowerCase().includes('aborted');

      if (isTimeout) {
        console.warn('Get incoming calls timed out.');
      } else {
        console.error('Get incoming calls failed:', error);
      }
      return {
        success: false,
        offline: !error.response && !isTimeout,
        error: error.response?.data?.detail || error.message || 'Failed to get incoming calls',
        calls: [],
        count: 0
      };
    }
  }

  async getQueueMetrics(agentId = '', extra = {}) {
    try {
      const params = new URLSearchParams();
      const resolvedAgentId = String(agentId || this.agentContext?.agentId || '').trim();
      if (resolvedAgentId) params.set('agentId', resolvedAgentId);
      const queueNames = Array.isArray(extra.queueNames) ? extra.queueNames.filter(Boolean).join(',') : '';
      if (queueNames) params.set('queueNames', queueNames);
      const url = params.toString() ? `/queue-metrics?${params.toString()}` : '/queue-metrics';
      const response = await apiClient.get(url, { timeout: QUEUE_METRICS_TIMEOUT_MS });
      return {
        success: true,
        ...(response.data || {})
      };
    } catch (error) {
      const errorCode = String(error?.response?.data?.code || '').trim();
      const resolvedAgentId = String(agentId || this.agentContext?.agentId || '').trim();
      if (errorCode === 'AGENT_EXTENSION_REQUIRED' && resolvedAgentId && !extra.presenceRecoveryAttempted) {
        const context = this.agentContext || {};
        const recovery = await this.updateAgentStatus(resolvedAgentId, context.status || 'notready', context);
        if (recovery?.success) {
          return this.getQueueMetrics(agentId, { ...extra, presenceRecoveryAttempted: true });
        }
      }

      const message = String(error?.message || '');
      const isTimeout =
        error?.code === 'ECONNABORTED' ||
        message.toLowerCase().includes('timeout') ||
        message.toLowerCase().includes('aborted');

      if (isTimeout) {
        console.warn(`Get queue metrics timed out after ${QUEUE_METRICS_TIMEOUT_MS}ms.`);
      } else {
        console.warn('Get queue metrics failed:', error);
      }
      return {
        success: false,
        timeout: isTimeout,
        error: error.response?.data?.detail || error.message || 'Failed to get queue metrics'
      };
    }
  }

  async saveComplaintDraft(queryId, payload) {
    const response = await apiClient.patch(
      `/complaints/${encodeURIComponent(queryId)}/draft`,
      payload,
      { timeout: 30000 }
    );
    return response.data;
  }

  // Accept incoming call
  async acceptIncomingCall(channelId, agentId) {
    try {
      console.log(`📲 Accepting incoming call: ${channelId} by agent: ${agentId}`);
      const response = await apiClient.post('/accept-call', {
        channelId,
        agentId
      });
      
      console.log(`✅ Incoming call accepted successfully`);
      const callInfo = response.data.callInfo || {};
      const resolvedStartTime = callInfo.startTime || callInfo.receivedAt || '';
      this.activeChannels.set(channelId, {
        number: callInfo.callerNumber || '',
        calledNumber: callInfo.calledNumber || '',
        userId: agentId || '',
        startTime: resolvedStartTime ? new Date(resolvedStartTime) : new Date(),
        status: 'connecting',
        duration: Number(callInfo.duration || 0),
        callerNumber: callInfo.callerNumber || '',
        acceptedBy: callInfo.acceptedBy || '',
        acceptedAt: callInfo.acceptedAt || '',
        queryId: callInfo.queryId || '',
        ivrUid: callInfo.ivrUid || '',
        linkedId: callInfo.linkedId || '',
        stableId: callInfo.stableId || callInfo.linkedId || callInfo.ivrUid || channelId,
        ...getCallResponseFields(callInfo)
      });
      return {
        success: true,
        callInfo
      };
    } catch (error) {
      console.error('Accept call failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.detail || error.message || 'Accept call failed'
      };
    }
  }

  // Send DTMF tones
  async sendDTMF(channelId, digits) {
    try {
      console.log(`🔢 Sending DTMF: ${digits} to channel: ${channelId}`);
      await apiClient.post('/dtmf', {
        channelId,
        digits
      });
      
      console.log(`✅ DTMF sent successfully`);
      return { success: true };
    } catch (error) {
      console.error('DTMF failed:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'DTMF failed';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Hangup a call
  async hangupCall(channelId, metadata = {}) {
    try {
      console.log(`📴 Hanging up call. Channel ID: ${channelId}`);
      await apiClient.post('/hangup', {
        action: metadata.action,
        agentId: metadata.agentId,
        channelId
      });
      
      this.activeChannels.delete(channelId);
      console.log(`✅ Call hung up successfully`);
      return { success: true };
    } catch (error) {
      console.error('Hangup failed:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Hangup failed';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Emergency hangup all calls
  async hangupAllCalls() {
    try {
      console.log(`🚨 Emergency hangup all calls`);
      const response = await apiClient.post('/hangup-all');
      
      this.activeChannels.clear();
      console.log(`✅ All calls hung up successfully`);
      return { success: true, results: response.data.results };
    } catch (error) {
      console.error('Emergency hangup failed:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Emergency hangup failed';
      return {
        success: false,
        error: errorMessage
      };
    }
  }

  // Get connection status
  isConnected() {
    return this.connectionStatus === 'connected';
  }

  // Get active channels
  getActiveChannels() {
    return Array.from(this.activeChannels.entries()).map(([channelId, info]) => ({
      channelId,
      ...info
    }));
  }

  // Clean up
  destroy() {
    this.stopHealthCheck();
    this.resetCurrentCall();
    this.activeChannels.clear();
  }
}

// Create singleton instance
const asteriskService = new AsteriskService();

export default asteriskService;
